// BOM expansion — turns order lines into per-component requirements,
// aggregating across lines that share components. Pure module, used by
// both the API (at submit time) and the web (live stock chips on draft).
/**
 * Aggregate requirements across all order lines. Components shared between
 * products are summed once.
 */
export function expandOrderBom(lines) {
    const totals = new Map();
    for (const line of lines) {
        if (!Number.isFinite(line.qty) || line.qty <= 0)
            continue;
        // Leaf lines consume their own SKU: an explicit Component SKU, OR a
        // finished PRODUCT with no recipe (empty BOM) — a ready-made good drawn
        // straight from its own stock rather than assembled from components.
        if (line.lineIsLeaf || line.bom.length === 0) {
            totals.set(line.productSkuId, (totals.get(line.productSkuId) ?? 0) + line.qty);
            continue;
        }
        for (const b of line.bom) {
            if (!Number.isFinite(b.qtyRequired) || b.qtyRequired <= 0)
                continue;
            const need = line.qty * b.qtyRequired;
            totals.set(b.componentSkuId, (totals.get(b.componentSkuId) ?? 0) + need);
        }
    }
    return [...totals.entries()].map(([componentSkuId, qtyRequired]) => ({
        componentSkuId,
        qtyRequired,
    }));
}
/**
 * Returns true if the aggregated requirement exceeds available stock for
 * any component. Used to set Order.insufficientParts at submit and to flip
 * it as stock changes.
 */
export function isInsufficient(requirements, available) {
    for (const r of requirements) {
        const have = available[r.componentSkuId] ?? 0;
        if (have < r.qtyRequired)
            return true;
    }
    return false;
}
/**
 * Plan an order against available stock: draw finished units first, build the
 * rest from components. `available` is the partner's `onHand − reserved` map
 * (excluding this order at submit time) and covers BOTH finished products and
 * components — they are distinct SKUs. Pure; mirrored by the API (submit) and
 * the web (draft split + banner).
 */
export function planFulfillment(lines, available) {
    // Aggregate demand + remember each product's BOM/leaf status.
    const demand = new Map();
    const meta = new Map();
    for (const l of lines) {
        if (!Number.isFinite(l.qty) || l.qty <= 0)
            continue;
        demand.set(l.productSkuId, (demand.get(l.productSkuId) ?? 0) + l.qty);
        if (!meta.has(l.productSkuId)) {
            meta.set(l.productSkuId, {
                bom: l.bom,
                isLeaf: !!l.lineIsLeaf || l.bom.length === 0,
            });
        }
    }
    const products = [];
    const stockReservations = [];
    const componentTotals = new Map();
    for (const [sku, d] of demand) {
        const { bom, isLeaf } = meta.get(sku);
        const avail = Math.max(0, available[sku] ?? 0);
        const fromStock = Math.min(d, avail);
        const toBuild = d - fromStock;
        products.push({ productSkuId: sku, demand: d, fromStock, toBuild, buildable: !isLeaf });
        if (isLeaf) {
            // A leaf/recipe-less line has no build path. Reserve the FULL demand from
            // own stock (optimistic) so an over-subscription is tracked; the split
            // above still shows the real from-stock vs shortfall for display.
            stockReservations.push({ skuId: sku, qty: d });
        }
        else {
            if (fromStock > 0)
                stockReservations.push({ skuId: sku, qty: fromStock });
            for (const b of bom) {
                if (toBuild > 0 && Number.isFinite(b.qtyRequired) && b.qtyRequired > 0) {
                    componentTotals.set(b.componentSkuId, (componentTotals.get(b.componentSkuId) ?? 0) + toBuild * b.qtyRequired);
                }
            }
        }
    }
    const componentReservations = [...componentTotals.entries()].map(([componentSkuId, qtyRequired]) => ({ componentSkuId, qtyRequired }));
    // Shortage check aggregates BOTH roles of a SKU against the same stock: a SKU
    // ordered directly (finished) AND needed by a BOM (component) must fit their
    // COMBINED demand, not each role independently (review P1).
    const combined = new Map();
    for (const s of stockReservations) {
        combined.set(s.skuId, (combined.get(s.skuId) ?? 0) + s.qty);
    }
    for (const c of componentReservations) {
        combined.set(c.componentSkuId, (combined.get(c.componentSkuId) ?? 0) + c.qtyRequired);
    }
    const shortages = [];
    let insufficient = false;
    for (const [sku, required] of combined) {
        const have = available[sku] ?? 0;
        if (have < required) {
            insufficient = true;
            shortages.push({ skuId: sku, required, available: have });
        }
    }
    return { products, stockReservations, componentReservations, shortages, insufficient };
}
/**
 * Distribute each product's finished-stock draw (from a FulfillmentPlan) across
 * the order lines that make up that product, greedily in line order. Returns the
 * from-stock quantity per input line (same order). Used to snapshot the
 * submit-time split so a submitted hybrid order shows the correct to-build
 * component requirement even after live stock has since moved (WMS-26 / P2).
 */
export function allocateFromStock(lines, products) {
    const remaining = new Map(products.map((p) => [p.productSkuId, Math.max(0, p.fromStock)]));
    return lines.map((l) => {
        const rem = remaining.get(l.productSkuId) ?? 0;
        const take = Math.min(Math.max(0, l.qty), rem);
        remaining.set(l.productSkuId, rem - take);
        return take;
    });
}
// Statuses where an order's OPEN FINISHED reservations still equal its
// submit-time finished-stock draw. Assembly bumps them to full demand;
// delivery/cancel releases them — so past this point a legacy line's split
// can't be reconstructed from reservations.
const PRE_ASSEMBLY_STATUSES = new Set([
    'DRAFT',
    'SUBMITTED',
    'ON_HOLD',
    'IN_ASSEMBLY',
]);
/**
 * Where a submitted order's per-line from-stock split should be read from when
 * rendering component requirements (WMS-26/P2):
 *  - `snapshot`     — the line carries `fromStockAtSubmit` (authoritative).
 *  - `reservations` — no snapshot but the split is 0 or reconstructable: either
 *                     the order was never submitted (a cancelled draft — plain
 *                     BOM, nothing drawn from stock), or it's still pre-assembly
 *                     so its open FINISHED reservations still reflect the split.
 *  - `unavailable`  — a *submitted* legacy order already past assembly:
 *                     reservations were bumped/released, so the historical split
 *                     can't be rebuilt.
 * The transition path backfills the snapshot before assembly/cancel, so a
 * submitted order only reaches `unavailable` if it was already past assembly at
 * rollout.
 */
export function fromStockSplitSource(status, hasSnapshot, wasSubmitted) {
    if (hasSnapshot)
        return 'snapshot';
    // Never submitted (e.g. a cancelled draft): no fulfillment split ever
    // existed — show the plain requested BOM (fromStock = 0), not missing history.
    if (!wasSubmitted)
        return 'reservations';
    if (PRE_ASSEMBLY_STATUSES.has(status))
        return 'reservations';
    return 'unavailable';
}
//# sourceMappingURL=order-bom.js.map